import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Home } from 'lucide-react';
import { UserManagement } from '@/components/admin/users/UserManagement';
import { AppointmentsScheduler } from '@/components/dashboard/AppointmentsScheduler';
import { AvailabilityList } from '@/components/dashboard/AvailabilityList';
import { OrganizationList } from '@/components/admin/organizations/OrganizationList';
import { DashboardStats } from '@/components/dashboard/DashboardStats';
import { QuestionnaireManagement } from '@/components/admin/questionnaires';
import { ExamTabsManagement } from '@/components/admin/exam-tabs/ExamTabsManagement';
import { LogsManagement } from '@/components/admin/logs/LogsManagement';
import { AdminCourseManagement } from '@/components/admin/courses/AdminCourseManagement';
import { AdminGroupManagement } from '@/components/admin/groups/AdminGroupManagement';
import { AdminTripManagement } from '@/components/admin/trips/AdminTripManagement';
import { AdminOfferManagement } from '@/components/admin/offers/AdminOfferManagement';
import { AdminHelpManagement } from '@/components/admin/helps/AdminHelpManagement';
import { WebsiteManagement } from '@/components/admin/website/WebsiteManagement';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useLanguage } from '@/contexts/LanguageContext';
import { CustomerExamList } from '@/components/examiner/CustomerExamList';
import { useAdminStats }from '@/hooks/use-admin-stats';

export function AdminDashboard() {
  const [showWebsiteManagement, setShowWebsiteManagement] = useState(false);
  const { t, dir } = useLanguage();
  const { stats, loading, error } = useAdminStats();

  return (
    <div className="space-y-4 md:space-y-6 lg:space-y-8" dir={dir}>
      {/* Header Section */}
      <div className="flex flex-col gap-2 md:gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight">
              {t('dashboard.admin.title')}
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
              {t('dashboard.admin.description')}
            </p>
          </div>
          <Button 
            variant="outline"
            onClick={() => setShowWebsiteManagement(true)}
            className="w-full sm:w-auto text-xs md:text-sm h-8 md:h-9"
            size="sm"
          >
            <Home className={`${dir === 'rtl' ? 'ml-1.5' : 'mr-1.5'} h-3.5 w-3.5 md:h-4 md:w-4`} />
            محتوى الرئيسية
          </Button>
        </div>
      </div>

      {/* Stats Grid - Responsive */}
      <div className="grid gap-3 md:gap-4 grid-cols-2 lg:grid-cols-4">
        {loading ? (
          <>
            <Card className="p-3 md:p-4 lg:p-6">
              <div className="flex items-center justify-center h-16 md:h-20">
                <Loader2 className="h-4 w-4 md:h-6 md:w-6 animate-spin" />
              </div>
            </Card>
            <Card className="p-3 md:p-4 lg:p-6">
              <div className="flex items-center justify-center h-16 md:h-20">
                <Loader2 className="h-4 w-4 md:h-6 md:w-6 animate-spin" />
              </div>
            </Card>
            <Card className="p-3 md:p-4 lg:p-6">
              <div className="flex items-center justify-center h-16 md:h-20">
                <Loader2 className="h-4 w-4 md:h-6 md:w-6 animate-spin" />
              </div>
            </Card>
            <Card className="p-3 md:p-4 lg:p-6">
              <div className="flex items-center justify-center h-16 md:h-20">
                <Loader2 className="h-4 w-4 md:h-6 md:w-6 animate-spin" />
              </div>
            </Card>
          </>
        ) : error ? (
          <Card className="p-3 md:p-4 lg:p-6 col-span-full">
            <div className="flex items-center justify-center h-16 md:h-20 text-red-600 text-xs md:text-sm">
              {error}
            </div>
          </Card>
        ) : (
          <DashboardStats stats={stats} />
        )}
      </div>

      {/* Tabs Container - Mobile Optimized */}
      <Card className="p-3 md:p-4 lg:p-6">
        <Tabs defaultValue="helps" className="space-y-4 md:space-y-6">
          {/* Mobile: Scrollable horizontal tabs */}
          <div className="overflow-x-auto -mx-3 px-3 pb-2">
            <TabsList className="inline-flex w-max md:w-full gap-1 md:gap-2 h-auto p-1" dir={dir}>
              <TabsTrigger 
                value="users" 
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.users')}
              </TabsTrigger>
              <TabsTrigger
                value="beneficiaries"
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.beneficiaries')}
              </TabsTrigger>
              <TabsTrigger
                value="questionnaires" 
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.questionnaires')}
              </TabsTrigger>
              <TabsTrigger 
                value="exam-tabs" 
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.examTabs')}
              </TabsTrigger>
              <TabsTrigger 
                value="appointments" 
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.appointments')}
              </TabsTrigger>
              <TabsTrigger 
                value="availability" 
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.availability')}
              </TabsTrigger>
              <TabsTrigger 
                value="logs" 
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.habits')}
              </TabsTrigger>
              <TabsTrigger 
                value="groups" 
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.groups')}
              </TabsTrigger>
              <TabsTrigger 
                value="courses" 
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.courses')}
              </TabsTrigger>
              <TabsTrigger 
                value="organizations" 
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.organizations')}
              </TabsTrigger>
              <TabsTrigger 
                value="trips" 
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.trips')}
              </TabsTrigger>
              <TabsTrigger 
                value="offers" 
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.offers')}
              </TabsTrigger>
              <TabsTrigger 
                value="helps" 
                className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 whitespace-nowrap md:flex-1"
              >
                {t('dashboard.admin.tabs.helps')}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="users" className="space-y-4 md:space-y-6">
            <UserManagement />
          </TabsContent>

          <TabsContent value="beneficiaries" className="space-y-4 md:space-y-6">
            <CustomerExamList allOrganizations />
          </TabsContent>

          <TabsContent value="questionnaires" className="space-y-4 md:space-y-6">
            <QuestionnaireManagement />
          </TabsContent>

          <TabsContent value="exam-tabs" className="space-y-4 md:space-y-6">
            <ExamTabsManagement />
          </TabsContent>

          <TabsContent value="appointments" className="space-y-4 md:space-y-6">
            <AppointmentsScheduler />
          </TabsContent>

          <TabsContent value="availability" className="space-y-4 md:space-y-6">
            <AvailabilityList />
          </TabsContent>

          <TabsContent value="logs" className="space-y-4 md:space-y-6">
            <LogsManagement />
          </TabsContent>

          <TabsContent value="groups" className="space-y-4 md:space-y-6">
            <AdminGroupManagement />
          </TabsContent>

          <TabsContent value="courses" className="space-y-4 md:space-y-6">
            <AdminCourseManagement />
          </TabsContent>

          <TabsContent value="organizations" className="space-y-4 md:space-y-6">
            <OrganizationList />
          </TabsContent>

          <TabsContent value="trips" className="space-y-4 md:space-y-6">
            <AdminTripManagement />
          </TabsContent>

          <TabsContent value="offers" className="space-y-4 md:space-y-6">
            <AdminOfferManagement />
          </TabsContent>

          <TabsContent value="helps" className="space-y-4 md:space-y-6">
            <AdminHelpManagement />
          </TabsContent>
        </Tabs>
      </Card>

      {/* Website Management Dialog */}
      <Dialog open={showWebsiteManagement} onOpenChange={setShowWebsiteManagement}>
        <DialogContent className="max-w-7xl h-[90vh] overflow-hidden flex flex-col p-0" dir={dir}>
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle className="text-2xl font-bold text-right">محتوى الرئيسية</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden px-6 pb-6">
            <WebsiteManagement />
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}